import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './components/LoginPage';
import DashboardLayout from './components/DashboardLayout';
import Dashboard from './pages/Dashboard';
import UserManagement from './pages/UserManagement';
import AdminManagement from './pages/AdminManagement';
import PrenupManagement from './pages/PrenupManagement';
import Settings from './pages/Settings';
import DocumentSettings from './pages/DocumentSettings';
import SmtpSettings from './pages/SmtpSettings';
import ChangePassword from './pages/ChangePassword';
import AuditLogs from './pages/AuditLogs';

import './App.css';

// Protected Route component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/prenup/SystemUser/Login" replace />;
  }
  
  // Check role-based access
  if (allowedRoles && allowedRoles.length > 0) {
    if (!allowedRoles.includes(user?.role)) {
      // Redirect to dashboard if user doesn't have permission
      return <Navigate to="/admin" replace />;
    }
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App min-h-screen bg-gray-50" dir="rtl">
          <Routes>
            {/* Default route redirects to login */}
            <Route path="/" element={<Navigate to="/prenup/SystemUser/Login" replace />} />
            
            {/* Login page with new URL structure */}
            <Route path="/prenup/SystemUser/Login" element={<LoginPage />} />
            
            {/* Protected admin routes */}
            <Route path="/admin" element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }>
              <Route index element={<Dashboard />} />
              <Route path="users" element={<UserManagement />} />
              <Route path="admins" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AdminManagement />
                </ProtectedRoute>
              } />
              <Route path="prenups" element={<PrenupManagement />} />
              <Route path="audit-logs" element={
                <ProtectedRoute allowedRoles={['admin', 'editor']}>
                  <AuditLogs />
                </ProtectedRoute>
              } />

              <Route path="settings" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <Settings />
                </ProtectedRoute>
              } />
              <Route path="settings/documents" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <DocumentSettings />
                </ProtectedRoute>
              } />
              <Route path="settings/smtp" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <SmtpSettings />
                </ProtectedRoute>
              } />
              <Route path="settings/change-password" element={
                <ProtectedRoute allowedRoles={['admin', 'editor']}>
                  <ChangePassword />
                </ProtectedRoute>
              } />
            </Route>
            
            {/* Redirect any unknown routes to login */}
            <Route path="*" element={<Navigate to="/prenup/SystemUser/Login" replace />} />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;